import { useEffect, useState } from 'react';
import $api from '@/http';
import { subscribeAvatarInvalidation, useAvatarMeta } from './useAvatarMeta';
import { AvatarCache } from './AvatarCache';

// data:-URL нужен для Bearer/CSP. Считаем UTF-16 верхнюю оценку удерживаемых строк.
const cache = new AvatarCache<string>(300, 8 * 1024 * 1024);
interface ImageRequest {
    controller: AbortController;
    promise: Promise<string | undefined>;
    resolve: (src?: string) => void;
    key: string;
    userKey: string;
    version?: number | string;
    size: number | 'full';
    users: number;
}
const inFlight = new Map<string, ImageRequest>();
const queue = new Set<ImageRequest>();
let activeRequests = 0;

function cancel(request: ImageRequest): void {
    request.controller.abort();
    queue.delete(request);
    if (inFlight.get(request.key) === request) inFlight.delete(request.key);
    request.resolve(undefined);
}

export function invalidateAvatarCache(userKey?: string): void {
    for (const key of [...cache.keys()]) {
        if (!userKey || key.startsWith(`${userKey}:`)) cache.delete(key);
    }
    for (const request of inFlight.values()) {
        if (!userKey || request.userKey === userKey) cancel(request);
    }
}
const unsubscribeInvalidation = subscribeAvatarInvalidation(invalidateAvatarCache);
if (import.meta.hot) import.meta.hot.dispose(() => {
    unsubscribeInvalidation();
    invalidateAvatarCache();
});

function blobToDataUrl(blob: Blob, signal: AbortSignal): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const cleanup = (): void => {
            reader.onload = reader.onerror = reader.onabort = null;
            signal.removeEventListener('abort', abort);
        };
        const fail = (): void => {
            cleanup();
            reject(reader.error ?? new Error('Чтение аватара отменено'));
        };
        const abort = (): void => { reader.abort(); fail(); };
        reader.onload = () => { cleanup(); resolve(reader.result as string); };
        reader.onerror = reader.onabort = fail;
        if (signal.aborted) { fail(); return; }
        signal.addEventListener('abort', abort, { once: true });
        reader.readAsDataURL(blob);
    });
}

export function buildAvatarUrl(userKey: string, version?: number | string, size?: number | string): string {
    const params = new URLSearchParams();
    if (version != null && version !== '') params.set('v', String(version));
    if (size != null && size !== '') params.set('size', String(size));
    const qs = params.toString();
    return `/addressBook/${encodeURIComponent(userKey)}/avatar${qs ? `?${qs}` : ''}`;
}

export function normalizeAvatarSize(size?: number | string): number | 'full' {
    if (size === 'full') return 'full';
    const value = Number(size);
    if (!Number.isFinite(value) || value <= 0) return 256;
    return [32, 64, 128, 256].find((candidate) => value <= candidate) ?? 256;
}

function drain(): void {
    while (activeRequests < 6 && queue.size) {
        const request = queue.values().next().value;
        if (!request) break;
        queue.delete(request);
        activeRequests++;
        void $api.get(buildAvatarUrl(request.userKey, request.version, request.size), {
            responseType: 'blob', suppressErrorNotification: true,
            signal: request.controller.signal, timeout: 15000,
        }).then((res) => blobToDataUrl(res.data as Blob, request.controller.signal)).then((src) => {
            if (request.controller.signal.aborted || inFlight.get(request.key) !== request) return;
            // Полноразмерное фото живёт только пока открыт просмотр, не в сессионном LRU.
            if (request.size !== 'full') cache.set(request.key, src, src.length * 2);
            request.resolve(src);
        }).catch(() => request.resolve(undefined)).finally(() => {
            if (inFlight.get(request.key) === request) inFlight.delete(request.key);
            activeRequests--;
            drain();
        });
    }
}

function acquireAvatar(userKey: string, version: number | string | undefined, size: number | 'full') {
    const key = `${userKey}:${version ?? ''}:${size}`;
    const cached = cache.get(key);
    if (cached) return { promise: Promise.resolve(cached), release: () => {} };
    let request = inFlight.get(key);
    if (!request) {
        let resolve!: ImageRequest['resolve'];
        const promise = new Promise<string | undefined>((done) => { resolve = done; });
        request = { controller: new AbortController(), promise, resolve, key, userKey, version, size, users: 0 };
        inFlight.set(key, request);
        queue.add(request);
    }
    const subscription = request;
    subscription.users++;
    drain();
    return {
        promise: subscription.promise,
        release: () => {
            subscription.users--;
            if (subscription.users === 0 && inFlight.get(key) === subscription) cancel(subscription);
        },
    };
}

/** Метаданные — источник истины; запросы разделяются подписчиками и отменяются без потребителей. */
export function useAvatarSrc(
    userKey?: string,
    hasImage?: boolean,
    version?: number | string,
    size?: number | string,
): string | undefined {
    const meta = useAvatarMeta(userKey);
    const effectiveHasImage = meta?.hasImage ?? hasImage;
    const effectiveVersion = meta ? meta.photoVersion ?? undefined : version;
    const canonicalSize = normalizeAvatarSize(size);
    const [image, setImage] = useState<{ key: string; src?: string }>();
    const key = `${userKey}:${effectiveVersion ?? ''}:${canonicalSize}`;

    useEffect(() => {
        if (!userKey || !meta || effectiveHasImage === false) {
            setImage(undefined);
            return;
        }
        let active = true;
        const subscription = acquireAvatar(userKey, effectiveVersion, canonicalSize);
        void subscription.promise.then((src) => { if (active) setImage({ key, src }); });
        return () => { active = false; subscription.release(); };
    }, [userKey, effectiveHasImage, effectiveVersion, canonicalSize, key, meta]);

    return !meta || effectiveHasImage === false || image?.key !== key ? undefined : image.src;
}
